/**
 * One artifact (f15.1): header, "Edit details", "Upload new version", and the
 * full, read-only Version history. Mirrors `artifact-detail.html`.
 *
 * f15.3 adds the Published / Private badge, Unpublish, the Status column and
 * the per-row "Publish vN" / "Roll back to vN" action. f15.4 adds the Access
 * codes card below Version history. f15.5 adds the External link row and the
 * Preview external page button. Deliberately absent until its feature lands:
 * Recent access (f15.6). No
 * control here edits or removes a version -- the server offers neither.
 */

import { useState } from 'react';
import { ArrowLeft, Download, EyeOff, Pencil, Upload } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { api, type ArtifactDetail, type ArtifactVersion } from '@/api/client';
import { useArtifact } from '@/api/queries';
import { PUBLISH_ARTIFACT, rowCan } from '@/session/permissions';
import { RecordUnavailable } from '@/ui/RecordUnavailable';

import {
  downloadName,
  formatDate,
  formatDateTime,
  formatSize,
  initialsOf,
  levelLabel,
  ownerPath,
  saveBlob,
  shortHash,
  typeLabel,
} from './artifactView';
import { AccessCodesCard } from './AccessCodes';
import { RecentAccessCard } from './RecentAccessCard';
import { EditArtifactDialog } from './EditArtifactDialog';
import { ExternalLinkRow, PreviewExternalPageButton } from './ExternalLink';
import {
  PublicationBadge,
  PublishActionButton,
  PublishVersionDialog,
  UnpublishDialog,
  VersionStatusPill,
} from './PublicationControls';
import { UploadVersionDialog } from './UploadVersionDialog';

function OwnerLink({ owner }: { owner: ArtifactDetail['owner'] }) {
  const path = ownerPath(owner);
  return path ? (
    <Link className="cell-link" to={path}>
      {owner.name}
    </Link>
  ) : (
    <span>{owner.name}</span>
  );
}

function VersionRow({
  artifact,
  version,
  mayPublish,
  onPublish,
}: {
  artifact: ArtifactDetail;
  version: ArtifactVersion;
  mayPublish: boolean;
  onPublish: (version: ArtifactVersion) => void;
}) {
  const latest = version.is_latest;
  const published = version.publication_status === 'PUBLISHED';
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function download() {
    setBusy(true);
    setFailed(false);
    try {
      const blob = await api.downloadArtifactVersion(artifact.id, version.id);
      saveBlob(blob, downloadName(artifact.title, version.version_number, version.content_type));
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className={published ? 'is-published-version' : undefined}>
      <td className="mono">
        {latest ? <b>v{version.version_number}</b> : `v${version.version_number}`}{' '}
        {latest && <span className="badge badge-primary">Latest</span>}
      </td>
      <td>
        <span className="row version-publisher">
          <span className="avatar sm" aria-hidden="true">
            {initialsOf(version.publisher.name)}
          </span>
          {version.publisher.name}
        </span>
      </td>
      <td>{formatDateTime(version.created_at)}</td>
      <td className="truncate">{version.release_note ?? <span className="hint">No note</span>}</td>
      <td className="num">{formatSize(version.byte_size)}</td>
      <td className="mono" title={version.sha256}>
        {shortHash(version.sha256)}
      </td>
      <td>
        <VersionStatusPill status={version.publication_status} />
      </td>
      <td>
        <div className="row-actions">
          {mayPublish && !published && (
            <PublishActionButton artifact={artifact} version={version} onClick={() => onPublish(version)} />
          )}
          <button
            type="button"
            className="btn btn-secondary btn-icon"
            title={failed ? 'Download failed — try again' : 'Download'}
            aria-label={`Download v${version.version_number}`}
            disabled={busy}
            onClick={() => void download()}
          >
            <Download aria-hidden="true" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export function ArtifactDetailPage() {
  const { artifactId = '' } = useParams();
  const detail = useArtifact(artifactId);
  const [dialog, setDialog] = useState<'upload' | 'edit' | 'unpublish' | null>(null);
  const [publishing, setPublishing] = useState<ArtifactVersion | null>(null);

  if (detail.isPending) {
    return (
      <div className="card" aria-busy="true" aria-label="Loading the artifact">
        <div className="card-body">
          {[0, 1, 2, 3].map((index) => (
            <span key={index} className="skeleton-bar group-skeleton-line" />
          ))}
        </div>
      </div>
    );
  }

  if (detail.error || !detail.data) {
    // A 404 never says whether the artifact is missing or merely not the caller's.
    return (
      <RecordUnavailable
        error={detail.error}
        backTo="/"
        backLabel="Home"
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const artifact = detail.data;
  const mayPublish = rowCan(artifact, PUBLISH_ARTIFACT);
  const back = ownerPath(artifact.owner);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="header-crumb">
            {back ? <Link to={back}>{artifact.owner.name}</Link> : <span>{artifact.owner.name}</span>}{' '}
            <span>/</span> <b>{artifact.title}</b>
          </div>
          <h1 className="page-title">
            {artifact.title} <PublicationBadge artifact={artifact} />
          </h1>
          <div className="page-sub">
            Hangs off <OwnerLink owner={artifact.owner} /> &middot; {typeLabel(artifact.content_type)}
          </div>
          <ExternalLinkRow artifact={artifact} />
        </div>
        <div className="page-actions">
          <PreviewExternalPageButton artifact={artifact} />
          {back && (
            <Link className="btn btn-secondary btn-sm" to={back}>
              <ArrowLeft aria-hidden="true" /> {artifact.owner.name}
            </Link>
          )}
          {mayPublish && (
            <>
              {artifact.published_state === 'PUBLISHED' && (
                <button type="button" className="btn btn-secondary" onClick={() => setDialog('unpublish')}>
                  <EyeOff aria-hidden="true" /> Unpublish
                </button>
              )}
              <button type="button" className="btn btn-secondary" onClick={() => setDialog('edit')}>
                <Pencil aria-hidden="true" /> Edit details
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setDialog('upload')}>
                <Upload aria-hidden="true" /> Upload new version
              </button>
            </>
          )}
        </div>
      </div>

      <div className="split-2-1">
        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h3>Version history</h3>
              <span className="hint">Versions are immutable — a change is always v(n+1)</span>
            </div>
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Publisher</th>
                    <th>Uploaded</th>
                    <th>Release note</th>
                    <th className="num">Size</th>
                    <th>Hash</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {artifact.versions.map((version) => (
                    <VersionRow
                      key={version.id}
                      artifact={artifact}
                      version={version}
                      mayPublish={mayPublish}
                      onPublish={setPublishing}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <AccessCodesCard artifact={artifact} mayPublish={mayPublish} />
          <RecentAccessCard artifactId={artifact.id} />
        </div>

        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h3>Artifact</h3>
            </div>
            <div className="card-body stack-sm">
              <div className="row artifact-fact">
                <span className="hint">Owning level</span>
                <span>{levelLabel(artifact.owner.type)}</span>
              </div>
              <div className="row artifact-fact">
                <span className="hint">Node</span>
                <OwnerLink owner={artifact.owner} />
              </div>
              <div className="row artifact-fact">
                <span className="hint">Content type</span>
                <span className="mono">{artifact.content_type}</span>
              </div>
              <div className="row artifact-fact">
                <span className="hint">Versions</span>
                <span>{artifact.version_count}</span>
              </div>
              <div className="row artifact-fact">
                <span className="hint">Created</span>
                <span>
                  {formatDate(artifact.created_at)}
                  {artifact.created_by ? ` · ${artifact.created_by.name}` : ''}
                </span>
              </div>
            </div>
            {artifact.description && (
              <div className="card-foot">
                <span className="hint">{artifact.description}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {dialog === 'upload' && <UploadVersionDialog artifact={artifact} onClose={() => setDialog(null)} />}
      {dialog === 'edit' && <EditArtifactDialog artifact={artifact} onClose={() => setDialog(null)} />}
      {dialog === 'unpublish' && <UnpublishDialog artifact={artifact} onClose={() => setDialog(null)} />}
      {publishing && (
        <PublishVersionDialog artifact={artifact} version={publishing} onClose={() => setPublishing(null)} />
      )}
    </>
  );
}
